// History's rows from the old workbook: the "Shipped Orders 2026" tab, A2000_Gold
// `dbo.vw_routing_history_excel`, loaded once and never refreshed. Each is a finished line of its
// own, read-only, never merged or de-duplicated against the app's lines.

import { asDay } from "./days";
import type { DivisionNamer } from "./divisions";
import { divisionNamer } from "./divisions";
import type { LineView } from "./assemble";
import { EMPTY_FIELDS } from "./assemble";

/**
 * A customer name with its spelling taken out: case, spaces and punctuation. The workbook was typed
 * by hand, so History holds one customer as "BELK", "Belk" and "B.E.L.K."; equal keys are one
 * customer. Words are not: "BELK OUTLET" is another account.
 */
export const customerSpelling = (name: unknown) =>
  String(name ?? "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "");

/** One workbook row, as the view gives it (lowercase columns). Dates are `YYYY-MM-DD`. */
export interface ExcelHistoryRow {
  source_row: number;
  source_sheet: string | null;
  customer_name: string | null;
  division: string | null;
  pt_date: string | null;
  start_date: string | null;
  cancel_date: string | null;
  ready_date: string | null;
  ready_date_text: string | null;
  po_pt_text: string | null;
  /** bigint, as text. */
  first_pick_ticket: string | null;
  qty: number | null;
  work_needed: string | null;
  production_notes: string | null;
  /** bigint, as text. */
  wms_shipment: string | null;
  routing_notes: string | null;
  routing_reference: string | null;
  sched_ship_date: string | null;
  sched_ship_text: string | null;
  inventory_notes: string | null;
  lead: string | null;
  timing_for_rework: string | null;
  inv: string | null;
  notes: string | null;
  finished_status: string | null;
  finished_on: string | null;
  finished_on_source: string | null;
  row_kind: string | null;
  is_duplicate: 0 | 1;
  has_odd_date: 0 | 1;
}

export const ROW_KINDS = ["Order", "Sample", "Transfer"] as const;
/** History's default: orders only. The app's own lines are all orders. */
export const DEFAULT_KINDS: string[] = ["Order"];

/** The one workbook date that is the real finish; any other is approximate. */
export const EXACT_FINISH_SOURCE = "Schedule Ship Date";

export const EXCEL_SOURCE_LABEL = "Excel 2026";

export interface ExcelInfo {
  sourceRow: number;
  sheet: string;
  kind: string;
  duplicate: boolean;
  oddDate: boolean;
  /** Which workbook date `finished.at` is, when it is not the scheduled ship date; else null. */
  approxFrom: string | null;
  poPt: string;
  firstPickTicket: string;
  inv: string;
}

const str = (v: string | null) => v ?? "";

/**
 * A workbook row as a History line: CO., Order # and WH stay empty; nothing is looked up but the
 * division's abbreviation, by its name (`divisions.ts`).
 */
export function excelLine(r: ExcelHistoryRow, abbrev: DivisionNamer = divisionNamer()): LineView {
  const finishedOn = asDay(r.finished_on);
  return {
    key: `xl-${r.source_row}`,
    stored: null,
    groupKey: "",
    // The old workbook tracked A2000 pick tickets.
    source: "A2000",
    deposco: null,
    wmsNo: "",
    pts: [],
    removed: [],
    corp: "",
    customerCode: "",
    customerName: str(r.customer_name),
    customerNames: r.customer_name ? [r.customer_name] : [],
    customers: r.customer_name ? [{ code: "", name: r.customer_name, source: "A2000" }] : [],
    div: "",
    division: str(r.division),
    divisions: r.division ? [r.division] : [],
    divs: r.division ? [{ corp: "", div: "", name: r.division, abbrev: abbrev({ name: r.division }) }] : [],
    wh: "",
    whs: [],
    ptDate: asDay(r.pt_date),
    ptDates: asDay(r.pt_date) ? [asDay(r.pt_date)!] : [],
    start: asDay(r.start_date),
    starts: [asDay(r.start_date) ?? ""],
    cancel: asDay(r.cancel_date),
    cancels: [asDay(r.cancel_date) ?? ""],
    qty: Number(r.qty ?? 0),
    value: null,
    wms: str(r.wms_shipment),
    dcs: [],
    orderNos: [],
    pos: [],
    mixed: null,
    review: "approved",
    changes: {},
    ptChanges: [],
    // No routing status: nothing it routes.
    routingCategory: "Not routed",
    fields: {
      ...EMPTY_FIELDS,
      readyDate: asDay(r.ready_date) ?? str(r.ready_date_text),
      workNeeded: str(r.work_needed),
      productionNotes: str(r.production_notes),
      // The workbook has no routing status: empty, not an invented "Not routed".
      routingStatus: "",
      routingNotes: str(r.routing_notes),
      routingRef: str(r.routing_reference),
      schedShip: asDay(r.sched_ship_date) ?? str(r.sched_ship_text),
      inventoryNotes: str(r.inventory_notes),
      lead: str(r.lead),
      timing: str(r.timing_for_rework),
      notes: str(r.notes),
    },
    carry: { sources: [], conflicts: {}, filled: [], from: "", saved: false },
    finished: { status: r.finished_status === "Cancelled" ? "Cancelled" : "Shipped", at: finishedOn, by: "", note: "" },
    allGone: false,
    noWms: false,
    excel: {
      sourceRow: r.source_row,
      sheet: str(r.source_sheet),
      kind: r.row_kind ?? "Order",
      duplicate: r.is_duplicate === 1,
      oddDate: r.has_odd_date === 1,
      approxFrom:
        finishedOn && r.finished_on_source !== EXACT_FINISH_SOURCE ? (r.finished_on_source ?? "unknown") : null,
      poPt: str(r.po_pt_text),
      firstPickTicket: str(r.first_pick_ticket),
      inv: str(r.inv),
    },
    outside: [],
    a2000: null,
    onPickup: null,
    shipment: null,
    pickupDate: null,
  };
}

/** Order, Sample or Transfer; an app line is an order. */
export const kindOf = (line: LineView) => line.excel?.kind ?? "Order";
